'use client'

import Image from 'next/image'
import Link from 'next/link'
import Footer from '@/components/Footer'

const modes = [
  {
    title: 'Classic',
    description: 'A clean fundamentals test built around survival, consistency, and controlled pressure.',
  },
  {
    title: 'Endless',
    description: 'Long-run economy, adaptation, and scaling intensity for players chasing deep runs.',
  },
  {
    title: 'Boss Minute',
    description: 'Ten seconds of setup, then fifty seconds of pure boss pressure.',
  },
  {
    title: 'Neon Shift',
    description: 'Phase-based hazards that reward perfect swaps and fast pattern recognition.',
  },
  {
    title: 'Pulse Arena',
    description: 'Pattern-heavy survival where arena timing matters as much as raw movement.',
  },
  {
    title: 'Extreme',
    description: 'Faster, denser, harsher survival built for high-risk runs and score pushes.',
  },
]

const features = [
  {
    title: 'One-thumb survival',
    description: 'Short, high-pressure runs designed to feel sharp, readable, and immediately replayable on Android.',
  },
  {
    title: 'Daily missions and streaks',
    description: 'Each day adds fresh credit objectives and progression pressure that rewards consistency.',
  },
  {
    title: 'Boost-driven prep',
    description: 'Spend earned credits on consumable boosts, then chain smart runs to build momentum.',
  },
  {
    title: 'Ship progression',
    description: 'Work toward premium ships with passives that change how each run feels.',
  },
]

const screenshots = [
  {
    src: '/neondrift/screenshots/01-home-screen.png',
    alt: 'Neon Drift home screen with credits, daily mission, and progression status',
    title: 'Home and daily loop',
    description: 'Mission prompts, streak pressure, credits, and quick entry back into a run.',
  },
  {
    src: '/neondrift/screenshots/02-mode-grid.png',
    alt: 'Neon Drift mode selection grid showing multiple playable modes',
    title: 'Mode selection',
    description: 'Six distinct modes that change pacing, challenge structure, and replay strategy.',
  },
  {
    src: '/neondrift/screenshots/04-gameplay-live.png',
    alt: 'Neon Drift live gameplay showing dodge-based survival and HUD elements',
    title: 'Live gameplay',
    description: 'Readable HUD, dense pressure, and precise dodge play in the middle of a live run.',
  },
  {
    src: '/neondrift/screenshots/03-store-boosts.png',
    alt: 'Neon Drift boosts store with consumables for upcoming runs',
    title: 'Boost store',
    description: 'Use credits for tactical consumables that shape the next few runs.',
  },
  {
    src: '/neondrift/screenshots/05-store-ships.png',
    alt: 'Neon Drift ship store showing unlockable ships and pricing',
    title: 'Ship unlocks',
    description: 'Progress toward better ships and different passive advantages over time.',
  },
  {
    src: '/neondrift/screenshots/00-splash-screen.png',
    alt: 'Neon Drift splash screen with the game logo and neon visual treatment',
    title: 'Launch identity',
    description: 'A neon-first visual system that carries from splash through gameplay.',
  },
]

export default function NeonDriftPage() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#04060d] text-white">
      <section className="relative isolate overflow-hidden px-6 pb-20 pt-28 sm:px-8 sm:pb-28 sm:pt-36">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(178,40,255,0.24),transparent_24%),radial-gradient(circle_at_22%_18%,rgba(20,239,255,0.2),transparent_20%),linear-gradient(180deg,#03050a_0%,#07101b_48%,#05070d_100%)]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/50 to-transparent" />
        <div className="absolute left-1/2 top-24 h-80 w-80 -translate-x-1/2 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute right-0 top-12 h-72 w-72 rounded-full bg-fuchsia-500/15 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl gap-14 lg:grid-cols-[minmax(0,1.02fr)_minmax(420px,0.98fr)] lg:items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-3 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-4 py-2 text-xs uppercase tracking-[0.32em] text-cyan-200">
              Android Coming Soon
            </div>

            <div className="space-y-5">
              <p className="font-mono text-sm uppercase tracking-[0.4em] text-fuchsia-200/80">
                Precision Arcade Survival
              </p>
              <h1 className="max-w-4xl text-5xl font-light leading-[0.92] tracking-[-0.05em] text-white sm:text-6xl lg:text-7xl">
                Survive.
                <span className="block bg-gradient-to-r from-cyan-300 via-white to-fuchsia-300 bg-clip-text text-transparent">
                  Adapt. Break your limits.
                </span>
              </h1>
              <p className="max-w-3xl text-lg leading-8 text-slate-300 sm:text-xl">
                Neon Drift is a high-speed Android survival game where every second matters. Dodge meteor waves, master distinct
                game modes, build your ship loadout, and come back daily for fresh credit missions and streak rewards.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="#android"
                className="inline-flex min-h-14 items-center justify-center rounded-full border border-cyan-300/40 bg-cyan-300 px-7 text-sm font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-200"
              >
                Android coming soon
              </a>
              <Link
                href="/neondrift/privacy-policy"
                className="inline-flex min-h-14 items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:border-white/35 hover:bg-white/10"
              >
                Privacy policy
              </Link>
              <Link
                href="/neondrift/support"
                className="inline-flex min-h-14 items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:border-white/35 hover:bg-white/10"
              >
                Support
              </Link>
            </div>

            <ul className="grid gap-3 pt-2 text-sm text-slate-300 sm:grid-cols-3">
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">Fast, one-thumb-friendly arcade survival</li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">Modes like Boss Minute, Neon Shift, and Extreme</li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">Daily missions, streaks, boosts, and ship progression</li>
            </ul>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-cyan-300/20 via-transparent to-fuchsia-400/20 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#07111f]/90 p-3 shadow-[0_30px_120px_rgba(0,0,0,0.45)]">
              <div className="relative aspect-video overflow-hidden rounded-[1.4rem] border border-white/10 bg-black">
                <Image
                  src="/neondrift/video/neondrift-showcase-remotion-poster.png"
                  alt="Neon Drift promo banner art"
                  fill
                  priority
                  className="object-cover object-center"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,6,13,0.08),rgba(4,6,13,0.24))]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-4">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Why it hooks</p>
            <h2 className="text-4xl font-light leading-tight tracking-[-0.04em] sm:text-5xl">
              Short runs. Sharp pressure. Real progression.
            </h2>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-8 text-base leading-8 text-slate-300 sm:text-lg">
            A single run can earn credits, push a daily mission forward, unlock a new streak reward, or move you closer to your next
            ship. The core loop stays readable and skill-based, while the progression layer gives players a reason to come back tomorrow.
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.03] px-6 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Features</p>
            <h2 className="mt-3 text-4xl font-light tracking-[-0.04em] sm:text-5xl">
              Built for replay, not just spectacle.
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {features.map((feature, index) => (
              <article
                key={feature.title}
                className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-7"
              >
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full border border-cyan-300/25 bg-cyan-300/10 font-mono text-sm text-cyan-200">
                  0{index + 1}
                </div>
                <h3 className="text-2xl font-light tracking-[-0.03em]">{feature.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300 sm:text-base">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Modes</p>
              <h2 className="mt-3 text-4xl font-light tracking-[-0.04em] sm:text-5xl">Pick your run.</h2>
            </div>
            <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
              Every mode changes the rhythm. Some reward consistency, some pressure instant adaptation, and some exist purely to test how
              much chaos you can still read.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {modes.map((mode) => (
              <article key={mode.title} className="rounded-[1.75rem] border border-white/10 bg-[#09101a] p-7 shadow-[0_12px_40px_rgba(0,0,0,0.24)]">
                <h3 className="text-2xl font-light tracking-[-0.03em] text-white">{mode.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300 sm:text-base">{mode.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.03] px-6 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Showcase</p>
            <h2 className="mt-3 text-4xl font-light tracking-[-0.04em] sm:text-5xl">See the current build in motion.</h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]">
            <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#050913] p-3">
              <video
                className="aspect-video w-full rounded-[1.3rem] border border-white/10 bg-black object-cover"
                src="/neondrift/video/neondrift-showcase-remotion.mp4"
                poster="/neondrift/video/neondrift-showcase-remotion-poster.png"
                controls
                preload="metadata"
              />
            </div>

            <div className="rounded-[2rem] border border-cyan-300/15 bg-cyan-300/5 p-8">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-200">Current promo cut</p>
              <h3 className="mt-4 text-3xl font-light tracking-[-0.04em]">Designed for fast mobile-first showcase.</h3>
              <p className="mt-4 text-sm leading-7 text-slate-300 sm:text-base">
                The short hero cut works for landing-page placement and store-style previews. The longer showcase captures the current
                gameplay loop, HUD readability, and progression surfaces from the active build.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Screens</p>
            <h2 className="mt-3 text-4xl font-light tracking-[-0.04em] sm:text-5xl">Screens from the current build.</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {screenshots.map((shot) => (
              <figure key={shot.title} className="overflow-hidden rounded-[1.8rem] border border-white/10 bg-[#08101a] p-4">
                <div className="overflow-hidden rounded-[1.3rem] border border-white/10 bg-black">
                  <Image
                    src={shot.src}
                    alt={shot.alt}
                    width={1200}
                    height={2400}
                    className="h-auto w-full"
                  />
                </div>
                <figcaption className="px-2 pb-2 pt-5">
                  <div className="text-xl font-light tracking-[-0.03em] text-white">{shot.title}</div>
                  <p className="mt-2 text-sm leading-7 text-slate-300">{shot.description}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="android" className="px-6 pb-20 pt-6 sm:px-8 sm:pb-24">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-[2.25rem] border border-cyan-300/20 bg-[linear-gradient(135deg,rgba(17,35,57,0.95),rgba(12,14,24,0.98))] p-10 text-center shadow-[0_24px_90px_rgba(0,0,0,0.34)] sm:p-14">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Android release</p>
          <h2 className="mt-4 text-4xl font-light tracking-[-0.04em] sm:text-5xl">
            Android coming soon.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
            The page is structured so the Android store link can be dropped in later without changing the flow.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              href="/neondrift/support"
              className="inline-flex min-h-14 items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:border-white/35 hover:bg-white/10"
            >
              Contact support
            </Link>
            <Link
              href="/neondrift/privacy-policy"
              className="inline-flex min-h-14 items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:border-white/35 hover:bg-white/10"
            >
              Read privacy policy
            </Link>
          </div>
        </div>
      </section>

      <Footer maxWidth="max-w-7xl" />
    </div>
  )
}
